import { useEffect, useRef, useState } from 'react';
import { breakpoints, Button, Column, Columns, Inline, LinkButton, Stack, vars } from '@etg/wings';
import { ContentWrapper, DangerousHtmlContent, Notification, variants } from '@eti/components';
import { useProperty, useSearch, useTranslation } from '@eti/providers';
import { useMediaQuery } from 'usehooks-ts';
import { css, etiColors, keyframes } from '@eti/styles';
import { SortAscending } from '@phosphor-icons/react';
import type { SortType } from '@eti/schema-types';
import { TripType as SchemaTripType } from '@eti/schema-types';
import TripNotAvailableErrorModal from '../../../../common/error-modals/TripNotAvailableErrorModal';
import { useFreeCancellationDaysAheadNotification } from '../../../../common/custom-hooks/freeCancellationDaysAheadNotification';
import { isDesktopViewport } from '../../../../utils/view';
import { quickSortType } from '../../../../constants/quickSortConstants';
import {
  getFlightResultToInsertAdFormAfter,
  getFlightsExcludingSponsoredTripTags,
  removeUndesiredTripsAndTags,
} from '../../utils/resultUtils';
import { addAdFormScriptTags } from '../adForm';
import MobileAppBanner from '../../../../common/mobile-app-banner/MobileAppBanner';
import ResultFiltersToggleButton from '../search-filters/v3/ResultFiltersToggleButton';
import ResultPageDebugTools from '../search-filters/debug/ResultPageDebugTools';
import { resetAllFilters } from '../search-filters/utils/searchFilters';
import ValidWithVoucherSwitch from '../ValidWithVoucherSwitch';
import type { Filters } from '../search-filters/v3/MobileFilters';
import QuickSortButtonBarV1 from '../v1/QuickSortButtonBar';
import { Wrapper } from '../v1/SearchResults';
import ResultSearchFiltersV1 from '../v1/ResultSearchFilters';
import DifferentChargingCurrencyNotification from '../../../../widgets/different-charging-currency-notification/DifferentChargingCurrencyNotification';
import type { TripCampaignType, TripsType, TripType } from '../../models';
import NoResults from './NoResults';
import QuickSortButtonBarV2 from './quickSorting/QuickSortButtonBar';
import ResultSearchFilters from './ResultSearchFilters';
import SearchResultsLoader from './SearchResultsLoader';
import { useResultPageContext } from './contexts/ResultPageContextProvider';
import { ResultTripCards } from './resultTripCards/ResultTripCards';
import ResultTripCard from './resultTripCards/ResultTripCard';
import { SelectedTripCard } from './resultTripCards/SelectedTripCard';
import { RESULT_TRIPS } from './resultTripCards/constants';
import { getQuickSortOptions, getSortingDropDownOptions } from './quickSorting/utils';
import FlightResultSkeleton from './skeleton/FlightResultSkeleton';
import { NUMBER_OF_RESULTS, Results, ResultWrapper } from './skeleton/ResultSkeleton';
import type {
  NearbyPricesWithoutSelectedTripVersionEnumTypesValues,
  NearbyPricesWithSelectedTripVersionEnumTypesValues,
} from './nearbyDatesPrices/components/NearbyDatesPrices';
import NearbyDatesPrices, {
  NearbyPricesWithoutSelectedTripVersionEnumTypes,
  NearbyPricesWithSelectedTripVersionEnumTypes,
} from './nearbyDatesPrices/components/NearbyDatesPrices';
import type { Search } from './model';
import FloatingSaveTripButton from './FloatingSaveTripButton';
import SelfTransferModal from './SelfTransferModal';

const resultsMoveAnimation = keyframes`
  0% {
    opacity: 0.5;
    transform: translateY(0);
  }
  10% {
    opacity: 0.5;
    transform: translateY(10px);
  }
  20% {
    opacity: 0.25;
    transform: translateY(20px);
  }
  30% {
    opacity: 0.25;
    transform: translateY(30px);
  }
  40% {
    opacity: 0.25;
    transform: translateY(40px);
  }
  50% {
    opacity: 0;
    transform: translateY(50px);
  }
  60% {
    opacity: 0.25;
    transform: translateY(35px);
  }
  70% {
    opacity: 0.5;
    transform: translateY(15px);
  }
  80% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const flightResultsInnerWrapperStyles = (shouldShowResultsLoader: boolean) => css`
  animation: ${resultsMoveAnimation} 0.8s;
  ${shouldShowResultsLoader && 'opacity: 0.5; pointer-events: none;'}
`;

const searchResultWrapperStyles = css`
  background-color: ${vars.colors.pageBackdrop};
  height: 100%;

  @media (min-width: ${breakpoints._970}) {
    padding-block-start: 24px;
  }
`;

const sortAndFilterControlsStyles = css`
  background: ${etiColors.white};
  padding: 8px;
  width: 100%;

  span {
    text-transform: capitalize;
  }

  @media (min-width: ${breakpoints._970}) {
    background: ${vars.colors.pageBackdrop};
    display: flex;
    justify-content: space-between;
    padding-left: 0;
    padding-right: 0;

    > button {
      max-width: 160px;
    }
  }
`;

const desktopFilterControlsWhenSidebarDisabled = css`
  align-items: center;
  border: none;
  border-radius: 0;
  display: flex;
  flex-grow: 1;
  font-size: 0.875rem;
  justify-content: space-between;
  padding: 8px;
  padding-left: 0;
`;

const sortingNotificationStyles = css`
  color: #737373;
  font-size: 0.875rem;
  line-height: 150%;
`;
const mobileAppBannerStyles = css`
  margin-bottom: 16px;
`;

const innerWrapperStyles = (hasSelectedTrip: boolean) => css`
  @media (min-width: ${breakpoints._560}) {
    ${!hasSelectedTrip && 'padding-top: 16px'};
  }
`;

const sortingButtonStyles = css`
  width: 100%;
`;

const differentChargingCurrencyNotificationStyles = css`
  padding-block-end: 8px;
`;

function removeSelectedTrip(trips: TripsType, selectedTrip: TripType) {
  return selectedTrip
    ? trips.filter((trip) => trip?.selectionKey !== selectedTrip.selectionKey)
    : trips;
}

interface SearchResultsProps {
  closeFiltersPanel: () => void;
  handleSearch: (keepFilters: boolean) => void;
  hasSelectedTrip: boolean;
  handleSort: () => void;
  isFiltersPanelOpen: boolean;
  isSearchLoading: boolean;
  isSelfServiceRebooking: boolean;
  isInitialTripSelectionLoading: boolean;
  onCloseTripNotAvailableModal: () => void;
  onShowValidWithVoucher: () => void;
  onSort: (value: string) => void;
  quickSortPrices: any;
  search: Search;
  setFetchMoreResults: () => void;
  shouldShowResultsLoader: boolean;
  shouldShowTripIsNotAvailableModal: boolean;
  shouldShowValidWithVoucher: boolean;
  toggleFiltersPanel: () => void;
  toggleSearchPanel: () => void;
}

const inlineStyles = css`
  display: inline-block;
  width: 100%;
`;

const SearchResults = ({
  closeFiltersPanel,
  handleSearch,
  hasSelectedTrip,
  handleSort,
  isFiltersPanelOpen,
  isSearchLoading,
  isSelfServiceRebooking,
  isInitialTripSelectionLoading,
  onCloseTripNotAvailableModal,
  onShowValidWithVoucher,
  onSort,
  search,
  quickSortPrices,
  setFetchMoreResults,
  shouldShowResultsLoader,
  shouldShowTripIsNotAvailableModal,
  shouldShowValidWithVoucher,
  toggleFiltersPanel,
  toggleSearchPanel,
}: SearchResultsProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { currentSearchData, setSearchData } = useSearch();
  const [isMobileSortDialogOpen, setIsMobileSortDialogOpen] = useState(false);
  const [isSelfTransferModalOpen, setIsSelfTransferModalOpen] = useState(false);
  const flightResultsWrapperRef = useRef(null);
  const isNearbyDatesPricesEnabled: boolean = p('Result.Page.NearbyPrices.Enabled');
  const NearbyPricesWithoutSelectedTripVersion: NearbyPricesWithoutSelectedTripVersionEnumTypesValues =
    p('Result.Page.NearbyPrices.WithoutSelectedTrip.Version');

  const NearbyPricesWithSelectedTripVersion: NearbyPricesWithSelectedTripVersionEnumTypesValues = p(
    'Result.Page.NearbyPrices.WithSelectedTrip.Version',
  );
  const {
    filteredFlightsCount,
    flightsCount,
    flights: flightResults,
    sponsoredTrips: sponsoredTripsData,
    tripCampaigns,
  } = search;

  const sponsoredTrips = removeUndesiredTripsAndTags(sponsoredTripsData);
  const { selectedTrip } = useResultPageContext();
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { selectedSortType } = currentSearchData || {};

  const freeCancellationDaysAheadNotification = useFreeCancellationDaysAheadNotification(
    search?.origin?.departuredAt,
  );

  const sortingLegalNotice = t('Result.Sorting.Notice');
  const isSkeletonEnabled = p('Result.Skeleton.Enabled');

  useEffect(() => {
    const showAdForm = p('IbeClient.Adform.Enable');
    const adFormMid = isDesktopViewport()
      ? p('IbeClient.Adform.Result.Desktop.Mid')
      : p('IbeClient.Adform.Result.Mobile.Mid');
    const shouldShowAdForm = showAdForm && adFormMid;
    const { current } = flightResultsWrapperRef;

    if (shouldShowAdForm && current) {
      const flightResultToInsertAdFormAfter = getFlightResultToInsertAdFormAfter(current);

      if (flightResultToInsertAdFormAfter) {
        addAdFormScriptTags(flightResultToInsertAdFormAfter, adFormMid, search);
      }
    }
  }, [p, search]);

  const handleResetAll = () => {
    setSearchData!({ filters: { ...resetAllFilters(currentSearchData?.filters) } });
  };
  const onFilter = (filter: any) => {
    setSearchData!({ filters: { ...currentSearchData?.filters, ...filter } });
  };

  const handleMobileSortClick = () => {
    setIsMobileSortDialogOpen(!isMobileSortDialogOpen);
  };

  const getTripCampaignForFlight = (flightResult: NonNullable<TripType>) =>
    tripCampaigns?.find(
      (tripCampaign: TripCampaignType) => tripCampaign?.selectionKey === flightResult?.selectionKey,
    );

  const [recommendedTrip, trips] =
    flightResults.length > 0 &&
    selectedSortType === quickSortType.RECOMMENDED &&
    flightResults[0] &&
    getTripCampaignForFlight(flightResults[0])
      ? [
          flightResults[0],
          getFlightsExcludingSponsoredTripTags(
            flightResults.slice(1, flightResults.length),
            selectedSortType,
          ),
        ]
      : [null, getFlightsExcludingSponsoredTripTags(flightResults, selectedSortType)];

  const notificationText = freeCancellationDaysAheadNotification
    ? t('BookingFlow.FreeCancellation.DaysAhead.Text')
    : t('BookingFlow.FreeCancellation.Text');

  const notificationTestId = freeCancellationDaysAheadNotification
    ? 'freeCancellationInformationDaysAhead-search-result'
    : 'freeCancellationInformation-search-result';

  const isDebugEnabled = window.spa_variables?.debugEnabled;

  const sortingDropdownOptions = getSortingDropDownOptions(search.type, t);
  const sortOptions = getQuickSortOptions(t);
  const quickSortOptions = [...sortOptions, ...sortingDropdownOptions];
  const selectedSortFieldLabel = quickSortOptions.find(
    (sortOption) => sortOption.value === selectedSortType,
  );

  const quickSortVersion = p('Result.Page.QuickSort.Version');
  const isSortingInformationEnabled = p('Result.SortingInformation.Enabled');
  const isFreeCancellationEnabled = p('BookingFlow.FreeCancellation.Enable');
  const isValidWithVoucherEnabled = p('Result.SelfServiceRebooking.ValidWithVoucherSwitch.Enable');
  const isMobileAppPromoBannerEnabled = p('IbeClient.MobileAppPromoBanner.Enabled');
  const isVISelfTansferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');

  const isTripTypeAllowedToNearbyDatesPrices = (tripType: SchemaTripType) => {
    return (
      Boolean(tripType === SchemaTripType.OneWay) || Boolean(tripType === SchemaTripType.Return)
    );
  };

  const shouldShowNearbyDatesPrices =
    isNearbyDatesPricesEnabled && isTripTypeAllowedToNearbyDatesPrices(trips[0].type);

  const shouldShowNearbyDatesWithoutSelectedTripAtTop =
    shouldShowNearbyDatesPrices &&
    !selectedTrip &&
    NearbyPricesWithoutSelectedTripVersion === NearbyPricesWithoutSelectedTripVersionEnumTypes.A;

  const shouldShowNearbyDatesWithoutSelectedTripBelowQuicksort =
    shouldShowNearbyDatesPrices &&
    !selectedTrip &&
    NearbyPricesWithoutSelectedTripVersion === NearbyPricesWithoutSelectedTripVersionEnumTypes.B;

  const shouldShowNearbyDatesWithSelectedTripBelowSelectedTripCard =
    shouldShowNearbyDatesPrices &&
    selectedTrip &&
    NearbyPricesWithSelectedTripVersion === NearbyPricesWithSelectedTripVersionEnumTypes.A;

  const isMobile = useMediaQuery(`(max-width: ${breakpoints._970})`);
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints._970})`);

  const SortAndFilterControls = () => {
    return (
      <Inline>
        {quickSortVersion === 2 && (
          <Inline className={sortAndFilterControlsStyles} noWrap spacing={8}>
            {isDesktop && p('Result.Page.V3.DesktopDrawerSearchResultFilters.Enabled') ? (
              <Inline className={desktopFilterControlsWhenSidebarDisabled}>
                <span>
                  {t(
                    'Result.SearchFilters.ShowingFilteredResults.Label',
                    filteredFlightsCount,
                    flightsCount,
                  )}
                </span>
                <LinkButton data-testid="clear-filters-link-button" onClick={handleResetAll}>
                  {t('Result.SearchFilters.ClearFilters.Button')}
                </LinkButton>
              </Inline>
            ) : null}

            <ResultFiltersToggleButton isOpen={isFiltersPanelOpen} onClick={toggleFiltersPanel} />

            {isMobile && (
              <Button
                className={sortingButtonStyles}
                data-testid="sorting-button"
                iconPrefix={<SortAscending aria-hidden />}
                onClick={handleMobileSortClick}
                size="small"
              >
                <span>{selectedSortFieldLabel?.labelKey ?? 'Sort'}</span>
              </Button>
            )}
          </Inline>
        )}
      </Inline>
    );
  };

  return (
    <div className={searchResultWrapperStyles}>
      {/* Case 1: Show SearchFilters Sidebar */}
      {!p('Result.Page.V3.DesktopDrawerSearchResultFilters.Enabled') && (
        <>
          <Inline>
            {isMobile && quickSortVersion === 2 && (
              <Inline className={sortAndFilterControlsStyles} noWrap spacing={8}>
                <ResultFiltersToggleButton
                  isOpen={isFiltersPanelOpen}
                  onClick={toggleFiltersPanel}
                />
                <Button
                  className={sortingButtonStyles}
                  data-testid="sorting-button"
                  iconPrefix={<SortAscending aria-hidden />}
                  onClick={handleMobileSortClick}
                  size="small"
                >
                  <span>{selectedSortFieldLabel?.labelKey ?? 'Sort'}</span>
                </Button>
              </Inline>
            )}
          </Inline>
          {isMobile && quickSortVersion === 1 && (
            <Inline className={inlineStyles}>
              {search && !isInitialTripSelectionLoading && (
                <Wrapper>
                  <ResultSearchFiltersV1
                    availableSortTypes={search.availableSortTypes}
                    debugFilters={search.availableFilters}
                    filteredFlightsCount={search.filteredFlightsCount}
                    flightsCount={search.flightsCount}
                    handleClose={closeFiltersPanel}
                    handleToggle={toggleFiltersPanel}
                    isOpen={isFiltersPanelOpen}
                    isSearchLoading={isSearchLoading}
                    onSort={handleSort}
                    resultSetMetaData={search.resultSetMetaData}
                    searchRoutes={search.routes}
                  />
                </Wrapper>
              )}
              <>
                <FloatingSaveTripButton />
              </>
            </Inline>
          )}

          <ContentWrapper>
            <Stack className={innerWrapperStyles(hasSelectedTrip)}>
              <Columns
                spacing={{
                  [breakpoints._0]: 0,
                  [breakpoints._970]: 24,
                }}
              >
                {(quickSortVersion === 2 || !isMobile) && (
                  <Column
                    data-testid="resultPage-searchFilters"
                    ref={sidebarRef}
                    width={{ [breakpoints._0]: '4/4', [breakpoints._970]: '3/12' }}
                  >
                    <ResultSearchFilters
                      filteredFlightsCount={search.filteredFlightsCount}
                      flightsCount={search.flightsCount}
                      isOpen={isFiltersPanelOpen}
                      onClear={handleResetAll}
                      onDone={closeFiltersPanel}
                      onFilter={onFilter}
                      resultSetMetaData={search.resultSetMetaData}
                      searchRoutes={search.routes}
                      selectedFilters={currentSearchData?.filters as Filters}
                      sidebarRef={sidebarRef}
                    />
                    {isDebugEnabled && (
                      <ResultPageDebugTools
                        availableSortTypes={search.availableSortTypes}
                        // @ts-ignore - WEBTECH-1361
                        debugFilters={search.availableFilters}
                        filteredFlightsCount={search.filteredFlightsCount}
                        flightsCount={search.flightsCount}
                        onFilter={onFilter}
                        selectedFilters={currentSearchData?.filters}
                      />
                    )}
                  </Column>
                )}

                <Column
                  data-testid="resultPage-searchResults"
                  width={{ [breakpoints._0]: '4/4', [breakpoints._970]: '9/12' }}
                >
                  <Stack spacing={8}>
                    {p('IbeClient.DifferentChargingCurrencyNotification.Enabled') && (
                      <DifferentChargingCurrencyNotification
                        className={differentChargingCurrencyNotificationStyles}
                      />
                    )}

                    {/* Nearby dates at the top */}
                    {shouldShowNearbyDatesWithoutSelectedTripAtTop && (
                      <NearbyDatesPrices
                        handleSearch={handleSearch}
                        hasSelectedTrip={selectedTrip}
                      />
                    )}

                    {p('Result.Page.QuickFilters.Enabled') &&
                      (quickSortVersion === 2 ? (
                        <QuickSortButtonBarV2
                          isOpen={isMobileSortDialogOpen}
                          journeyType={search.type}
                          onDismiss={setIsMobileSortDialogOpen}
                          onSort={onSort}
                          selectedSortType={selectedSortType as SortType}
                          sortPrices={quickSortPrices}
                        />
                      ) : (
                        <div data-testid="Quicksort-button-bar-container-v1">
                          <QuickSortButtonBarV1
                            onSort={onSort}
                            selectedSortType={selectedSortType as SortType}
                            sortPrices={quickSortPrices}
                          />
                        </div>
                      ))}

                    {isDesktop && isSortingInformationEnabled && (
                      <div className={sortingNotificationStyles}>
                        <DangerousHtmlContent content={sortingLegalNotice} />
                      </div>
                    )}

                    {/* Nearby dates below Quick sort */}
                    {shouldShowNearbyDatesWithoutSelectedTripBelowQuicksort && (
                      <NearbyDatesPrices
                        handleSearch={handleSearch}
                        hasSelectedTrip={selectedTrip}
                      />
                    )}

                    {isSelfServiceRebooking && isValidWithVoucherEnabled && (
                      <ValidWithVoucherSwitch
                        onShowValidWithVoucher={onShowValidWithVoucher}
                        shouldShowValidWithVoucher={shouldShowValidWithVoucher}
                      />
                    )}
                    {(freeCancellationDaysAheadNotification || isFreeCancellationEnabled) && (
                      <Notification dataTestId={notificationTestId} variant={variants.CONFIRMATION}>
                        <DangerousHtmlContent content={notificationText} />
                      </Notification>
                    )}
                    {shouldShowResultsLoader && (
                      <SearchResultsLoader shouldShowResultsLoader={shouldShowResultsLoader} />
                    )}
                    {isSkeletonEnabled && shouldShowResultsLoader && (
                      <Results>
                        {[...Array(NUMBER_OF_RESULTS)].map((_, index) => (
                          <ResultWrapper key={index}>
                            <FlightResultSkeleton />
                          </ResultWrapper>
                        ))}
                      </Results>
                    )}
                    {!shouldShowResultsLoader && (
                      <div
                        className={flightResultsInnerWrapperStyles(shouldShowResultsLoader)}
                        key={selectedSortType}
                      >
                        <Stack>
                          {selectedTrip && (
                            <SelectedTripCard
                              setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                              trip={selectedTrip}
                            />
                          )}
                          {/* Selected trip (from meta) Nearby dates below Selected trip card, and with RECOMMENDED filter */}
                          {selectedSortType === quickSortType.RECOMMENDED &&
                            shouldShowNearbyDatesWithSelectedTripBelowSelectedTripCard && (
                              <NearbyDatesPrices
                                handleSearch={handleSearch}
                                hasSelectedTrip={selectedTrip}
                              />
                            )}
                          {selectedSortType === quickSortType.RECOMMENDED && (
                            <Stack>
                              {recommendedTrip && (
                                <ResultTripCard
                                  key={recommendedTrip.selectionKey}
                                  resultTripType={RESULT_TRIPS.RECOMMENDED}
                                  setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                                  trip={recommendedTrip}
                                  tripCampaign={getTripCampaignForFlight(recommendedTrip)}
                                />
                              )}
                              {sponsoredTrips && (
                                <ResultTripCards
                                  resultTripType={RESULT_TRIPS.SPONSORED}
                                  setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                                  trips={removeSelectedTrip(sponsoredTrips, selectedTrip)}
                                />
                              )}
                            </Stack>
                          )}
                          <div ref={flightResultsWrapperRef}>
                            {isMobileAppPromoBannerEnabled && flightResults.length > 1 && (
                              <MobileAppBanner
                                className={mobileAppBannerStyles}
                                isStartPage={false}
                              />
                            )}
                            {/* Selected trip (from meta) Nearby dates below Selected trip card, and without RECOMMENDED filter */}
                            {!(selectedSortType === quickSortType.RECOMMENDED) &&
                              shouldShowNearbyDatesWithSelectedTripBelowSelectedTripCard && (
                                <NearbyDatesPrices
                                  handleSearch={handleSearch}
                                  hasSelectedTrip={selectedTrip}
                                />
                              )}
                            <ResultTripCards
                              setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                              tripCampaigns={tripCampaigns}
                              trips={removeSelectedTrip(trips, selectedTrip)}
                            />
                          </div>
                        </Stack>
                      </div>
                    )}
                    <NoResults
                      filteredFlightsCount={filteredFlightsCount}
                      flightResults={flightResults}
                      flightsCount={flightsCount}
                      handleFilter={onFilter}
                      isSearchLoading={isSearchLoading}
                      resetAllFilters={resetAllFilters}
                      selectedFilters={currentSearchData?.filters}
                      setFetchMoreResults={setFetchMoreResults}
                      toggleSearchPanel={toggleSearchPanel}
                    />
                  </Stack>
                </Column>
              </Columns>
            </Stack>

            <TripNotAvailableErrorModal
              isOpen={shouldShowTripIsNotAvailableModal}
              onPrimaryAction={onCloseTripNotAvailableModal}
            />
          </ContentWrapper>
        </>
      )}

      {/* Case 2: Show SearchFilters Drawer */}
      {p('Result.Page.V3.DesktopDrawerSearchResultFilters.Enabled') && (
        <>
          {isMobile && quickSortVersion === 1 && (
            <Inline className={inlineStyles}>
              {search && !isInitialTripSelectionLoading && (
                <Wrapper>
                  <ResultSearchFiltersV1
                    availableSortTypes={search.availableSortTypes}
                    debugFilters={search.availableFilters}
                    filteredFlightsCount={search.filteredFlightsCount}
                    flightsCount={search.flightsCount}
                    handleClose={closeFiltersPanel}
                    handleToggle={toggleFiltersPanel}
                    isOpen={isFiltersPanelOpen}
                    isSearchLoading={isSearchLoading}
                    onSort={handleSort}
                    resultSetMetaData={search.resultSetMetaData}
                    searchRoutes={search.routes}
                  />
                </Wrapper>
              )}
              <>
                <FloatingSaveTripButton />
              </>
            </Inline>
          )}

          {isMobile ? <SortAndFilterControls /> : null}

          <ContentWrapper>
            {isDesktop ? <SortAndFilterControls /> : null}

            <Stack className={innerWrapperStyles(hasSelectedTrip)}>
              <div>
                {(quickSortVersion === 2 || !isDesktop) && (
                  <div data-testid="resultPage-searchFilters" ref={sidebarRef}>
                    <ResultSearchFilters
                      filteredFlightsCount={search.filteredFlightsCount}
                      flightsCount={search.flightsCount}
                      isOpen={isFiltersPanelOpen}
                      onClear={handleResetAll}
                      onDone={closeFiltersPanel}
                      onFilter={onFilter}
                      resultSetMetaData={search.resultSetMetaData}
                      searchRoutes={search.routes}
                      selectedFilters={currentSearchData?.filters as Filters}
                      sidebarRef={sidebarRef}
                    />
                    {isDebugEnabled && (
                      <ResultPageDebugTools
                        availableSortTypes={search.availableSortTypes}
                        // @ts-ignore - WEBTECH-1361
                        debugFilters={search.availableFilters}
                        filteredFlightsCount={search.filteredFlightsCount}
                        flightsCount={search.flightsCount}
                        onFilter={onFilter}
                        selectedFilters={currentSearchData?.filters}
                      />
                    )}
                  </div>
                )}

                <div data-testid="resultPage-searchResults">
                  <Stack spacing={8}>
                    {p('IbeClient.DifferentChargingCurrencyNotification.Enabled') && (
                      <DifferentChargingCurrencyNotification
                        className={differentChargingCurrencyNotificationStyles}
                      />
                    )}

                    {/* Nearby dates at the top */}
                    {shouldShowNearbyDatesWithoutSelectedTripAtTop && (
                      <NearbyDatesPrices
                        handleSearch={handleSearch}
                        hasSelectedTrip={selectedTrip}
                      />
                    )}

                    {p('Result.Page.QuickFilters.Enabled') &&
                      (quickSortVersion === 2 ? (
                        <QuickSortButtonBarV2
                          isOpen={isMobileSortDialogOpen}
                          journeyType={search.type}
                          onDismiss={setIsMobileSortDialogOpen}
                          onSort={onSort}
                          selectedSortType={selectedSortType as SortType}
                          sortPrices={quickSortPrices}
                        />
                      ) : (
                        <div data-testid="Quicksort-button-bar-container-v1">
                          <QuickSortButtonBarV1
                            onSort={onSort}
                            selectedSortType={selectedSortType}
                            sortPrices={quickSortPrices}
                          />
                        </div>
                      ))}

                    {isDesktop && isSortingInformationEnabled && (
                      <div className={sortingNotificationStyles}>
                        <DangerousHtmlContent content={sortingLegalNotice} />
                      </div>
                    )}

                    {/* Nearby dates below Quick sort */}
                    {shouldShowNearbyDatesWithoutSelectedTripBelowQuicksort && (
                      <NearbyDatesPrices
                        handleSearch={handleSearch}
                        hasSelectedTrip={selectedTrip}
                      />
                    )}

                    {isSelfServiceRebooking && isValidWithVoucherEnabled && (
                      <ValidWithVoucherSwitch
                        onShowValidWithVoucher={onShowValidWithVoucher}
                        shouldShowValidWithVoucher={shouldShowValidWithVoucher}
                      />
                    )}
                    {(freeCancellationDaysAheadNotification || isFreeCancellationEnabled) && (
                      <Notification dataTestId={notificationTestId} variant={variants.CONFIRMATION}>
                        <DangerousHtmlContent content={notificationText} />
                      </Notification>
                    )}
                    {shouldShowResultsLoader && (
                      <SearchResultsLoader shouldShowResultsLoader={shouldShowResultsLoader} />
                    )}
                    {isSkeletonEnabled && shouldShowResultsLoader && (
                      <Results>
                        {[...Array(NUMBER_OF_RESULTS)].map((_, index) => (
                          <ResultWrapper key={index}>
                            <FlightResultSkeleton />
                          </ResultWrapper>
                        ))}
                      </Results>
                    )}
                    {!shouldShowResultsLoader && (
                      <div
                        className={flightResultsInnerWrapperStyles(shouldShowResultsLoader)}
                        key={selectedSortType}
                      >
                        <Stack>
                          {selectedTrip && (
                            <SelectedTripCard
                              setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                              trip={selectedTrip}
                            />
                          )}
                          {/* Selected trip (from meta) Nearby dates below Selected trip card, and with RECOMMENDED filter */}
                          {selectedSortType === quickSortType.RECOMMENDED &&
                            shouldShowNearbyDatesWithSelectedTripBelowSelectedTripCard && (
                              <NearbyDatesPrices
                                handleSearch={handleSearch}
                                hasSelectedTrip={selectedTrip}
                              />
                            )}
                          {selectedSortType === quickSortType.RECOMMENDED && (
                            <Stack>
                              {recommendedTrip && (
                                <ResultTripCard
                                  key={recommendedTrip.selectionKey}
                                  resultTripType={RESULT_TRIPS.RECOMMENDED}
                                  setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                                  trip={recommendedTrip}
                                  tripCampaign={getTripCampaignForFlight(recommendedTrip)}
                                />
                              )}
                              {sponsoredTrips && (
                                <ResultTripCards
                                  resultTripType={RESULT_TRIPS.SPONSORED}
                                  setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                                  trips={removeSelectedTrip(sponsoredTrips, selectedTrip)}
                                />
                              )}
                            </Stack>
                          )}
                          <div ref={flightResultsWrapperRef}>
                            {isMobileAppPromoBannerEnabled && flightResults.length > 1 && (
                              <MobileAppBanner
                                className={mobileAppBannerStyles}
                                isStartPage={false}
                              />
                            )}
                            {/* Selected trip (from meta) Nearby dates below Selected trip card, and without RECOMMENDED filter */}
                            {!(selectedSortType === quickSortType.RECOMMENDED) &&
                              shouldShowNearbyDatesWithSelectedTripBelowSelectedTripCard && (
                                <NearbyDatesPrices
                                  handleSearch={handleSearch}
                                  hasSelectedTrip={selectedTrip}
                                />
                              )}
                            <ResultTripCards
                              setIsSelfTransferModalOpen={setIsSelfTransferModalOpen}
                              tripCampaigns={tripCampaigns}
                              trips={removeSelectedTrip(trips, selectedTrip)}
                            />
                          </div>
                        </Stack>
                      </div>
                    )}
                    <NoResults
                      filteredFlightsCount={filteredFlightsCount}
                      flightResults={flightResults}
                      flightsCount={flightsCount}
                      handleFilter={onFilter}
                      isSearchLoading={isSearchLoading}
                      resetAllFilters={resetAllFilters}
                      selectedFilters={currentSearchData?.filters}
                      setFetchMoreResults={setFetchMoreResults}
                      toggleSearchPanel={toggleSearchPanel}
                    />
                  </Stack>
                </div>
              </div>
            </Stack>

            <TripNotAvailableErrorModal
              isOpen={shouldShowTripIsNotAvailableModal}
              onPrimaryAction={onCloseTripNotAvailableModal}
            />
          </ContentWrapper>
        </>
      )}
      {isVISelfTansferDealEnabled && (
        <SelfTransferModal
          isOpen={isSelfTransferModalOpen}
          setIsOpen={setIsSelfTransferModalOpen}
        />
      )}
    </div>
  );
};

export default SearchResults;
